import { css } from '@emotion/react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { useAlert, Button, FormRow, useForm } from '@frontend/design-system';
import { DurationTimeUnitSelection } from '../../../components/duration-time-unit-selection';
import {
  convertMinutesToDaysAndHours,
  convertDaysAndHoursToMinutes,
  getMaximumDurationError,
  APPOINTMENT_LEAD_TIME_MAX_HOURS,
  APPOINTMENT_LEAD_TIME_MAX_DAYS,
} from '../../../components/duration-time-unit-selection/helpers';
import { bookingSiteSetupLeadTimeTrackingIds } from '../trackingIds';

const { saveButton } = bookingSiteSetupLeadTimeTrackingIds;

type BookingSiteAppointmentLeadTimeProps = {
  locationId: string;
  leadDuration: number;
};

export const AppointmentLeadTimeForm = ({ locationId, leadDuration, ...rest }: BookingSiteAppointmentLeadTimeProps) => {
  const { t } = useTranslation('schedule-settings');
  const alert = useAlert();

  const { mutateAsync, isLoading: isUpdating } = SchedulerV3.Mutations.useUpdateLocationSettingsMutation();

  const { values, getFieldProps } = useForm({
    fields: {
      advanceBookingTime: {
        type: 'number',
        value: String(convertMinutesToDaysAndHours(leadDuration).duration),
        allowDecimal: false,
        allowNegative: false,
        validateOnChange: true,
      },
      timeUnit: {
        type: 'dropdown',
        value: convertMinutesToDaysAndHours(leadDuration).timeUnit,
      },
    },
  });

  const amountInMinutes = convertDaysAndHoursToMinutes(
    Number(values.advanceBookingTime),
    values.timeUnit as 'days' | 'hours'
  );

  const updateLeadTime = async () => {
    try {
      const locationSettingResponse = await mutateAsync({
        locationId: locationId,
        leadDuration: amountInMinutes,
      });
      alert.success(t('Advance booking duration updated successfully'));
      return Promise.resolve(locationSettingResponse);
    } catch (error) {
      console.error('Error updating lead duration:', error);
      alert.error(t('Failed to update advance booking duration'));
      return Promise.reject(error);
    }
  };

  const errorText = getMaximumDurationError({
    value: Number(values.advanceBookingTime),
    timeUnit: values.timeUnit as 'days' | 'hours',
    maxHours: APPOINTMENT_LEAD_TIME_MAX_HOURS,
    maxDays: APPOINTMENT_LEAD_TIME_MAX_DAYS,
  });

  const hasError = Boolean(errorText);

  const disabled = leadDuration === amountInMinutes || hasError;

  return (
    <FormRow css={formRowStyle} {...rest}>
      <DurationTimeUnitSelection
        durationFieldProps={getFieldProps('advanceBookingTime')}
        timeUnitFieldProps={getFieldProps('timeUnit')}
        errorText={errorText}
      />
      <Button
        trackingId={saveButton}
        variant='primary'
        size='large'
        onClick={updateLeadTime}
        disabled={disabled}
        loading={isUpdating}
      >
        {t('Save')}
      </Button>
    </FormRow>
  );
};

const formRowStyle = css({
  marginTop: theme.spacing(0.5),
  marginBottom: theme.spacing(0.5),
  [`@media (max-width: ${breakpoints.small.max}px)`]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    '& > *': {
      marginBottom: theme.spacing(1),
    },
    '& > *:last-child': {
      marginBottom: 0,
    },
  },
});
